/**
 * Synthetic factor correlation (plan §3.3 · §4.4).
 *
 * ρ is a cosine similarity between two factor-loading vectors, EXCEPT that the
 * denominator carries each security's idiosyncratic residual while the numerator
 * does not (see universe/factor-norm.ts). That asymmetry is the whole point: two
 * distinct securities with identical loadings still correlate below 1, because
 * they retain independent idiosyncratic risk. A "simplification" of `nrm` to the
 * plain vector length would pass symmetry, self-correlation, range and every
 * fixture pair whose loadings differ — which is why §4.4 calls out the identical
 * -vector case as its own assertion.
 *
 * Rounding happens BEFORE clamping, exactly as the demo does it: rounding after
 * would let 0.954 survive as 0.95 rather than being clamped from 0.95.
 *
 * Under D1 correlation became an observed FACT for live data (plan §6.4) — this
 * module is the synthetic derivation only. The `corr()` call shape is unchanged
 * across both, which is what lets `apps/api` and this package share one symbol.
 */
import type { DiversificationMember, Ticker } from '@meridian/contracts';

import { COLLATOR } from '../constants/collation';
import {
  CORR_CLAMP_HI,
  CORR_CLAMP_LO,
  CORR_PRECISION,
  FACTOR_DIMENSIONS,
} from '../constants/correlation';

export { FACTOR_DIMENSIONS };

/** The minimum a security must expose to be correlated against another. */
export interface FactorSecurity {
  readonly ticker: Ticker;
  readonly f: readonly number[];
  readonly nrm: number;
}

/** Scored candidate from a pairwise ρ scan; preserves the input row type. */
export type RankedCorrelation<T extends FactorSecurity = FactorSecurity> = DiversificationMember & {
  readonly candidate: T;
};

/**
 * Subject excluded, sorted by ρ descending. Generic over the candidate row so
 * callers that need sector/return fields keep them without an unsafe cast.
 */
export function rankByCorrelation<T extends FactorSecurity>(
  subject: FactorSecurity,
  candidates: readonly T[],
): readonly RankedCorrelation<T>[] {
  return (
    candidates
      .filter((c) => c.ticker !== subject.ticker)
      .map((candidate) => ({
        ticker: candidate.ticker,
        rho: corr(subject, candidate),
        candidate,
      }))
      /* Ticker tie-break: a total order so ranked lists are pure functions of the
       row SET, not of input order (which SQL does not guarantee without ORDER BY). */
      .sort((a, b) => b.rho - a.rho || COLLATOR.compare(a.ticker, b.ticker))
  );
}

const ROUND_SCALE = Math.pow(10, CORR_PRECISION);

/**
 * The loadings as a FIXED-LENGTH tuple. Under `noUncheckedIndexedAccess` a
 * `readonly number[]` index yields `number | undefined`, so dotting one costs
 * five non-null assertions — which style §4 bans, and rightly: they would be the
 * only thing standing between a short vector and a silent partial dot product.
 * Narrowing once, immediately after the length check, moves the guarantee into
 * the type system and leaves `dot` assertion-free.
 */
type FactorVector = readonly [number, number, number, number, number];

function asFactorVector(f: readonly number[], side: string): FactorVector {
  if (f.length !== FACTOR_DIMENSIONS) {
    throw new Error(
      `corr: ${side} needs ${String(FACTOR_DIMENSIONS)} factor loadings, got ${String(f.length)}`,
    );
  }
  return f as FactorVector;
}

function dot(a: FactorVector, b: FactorVector): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3] + a[4] * b[4];
}

/**
 * Synthetic ρ for a pair, in `[CORR_CLAMP_LO, CORR_CLAMP_HI]`, 2 dp.
 * Identity is by TICKER, not by object reference — two structurally equal rows
 * read from different payloads are the same security.
 */
export function corr(a: FactorSecurity, b: FactorSecurity): number {
  return corrImpl(a, b);
}

/* ────────────────────────────────────────────────────────────────────────────
 * LIVE correlation (plan §6.4 · §6.5b). Everything above this line is the
 * SYNTHETIC factor derivation; everything below observes ρ from aligned daily
 * log returns and keeps it RAW in [-1, 1].
 *
 * The two must never be unified into one "correlation" helper. They differ in
 * inputs (5 loadings vs an aligned return vector), in domain ([-0.85, 0.95] vs
 * [-1, 1]) and in precision policy (2 dp vs full). Clamping a live ρ would
 * silently amputate the tails that ranking and extremes logic exist to find.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Anything indexable and length-bearing — a plain array or a `Float64Array`. */
export type NumericVector = ArrayLike<number>;

const MIN_OBSERVATIONS = 2;

class VectorLengthMismatchError extends Error {
  public constructor(a: number, b: number) {
    super(`pearson: vectors must be the same length (${String(a)} vs ${String(b)})`);
    this.name = 'VectorLengthMismatchError';
  }
}

/**
 * Zero-mean, unit-variance copy of `v`, or `null` when ρ is undefined for it —
 * fewer than two observations, or no variance at all (a security that closed at
 * the same price every day in the window).
 *
 * Uses the POPULATION standard deviation, so `pearson` reduces exactly to the
 * mean dot product of two z-vectors. That identity is the whole reason the
 * nightly correlation job can pre-standardize once per window and then treat
 * every one of ~134k pairs as a dot product (plan §6.4.1 step 9); a sample
 * (n−1) divisor here would leave a systematic n/(n−1) bias between the kernel
 * and this reference, small enough to survive a smoke test at 756 observations
 * and large enough to fail the 6-dp parity assertion.
 *
 * Note this differs from `ann_vol`, which §6.5b defines with the SAMPLE stddev.
 * Different statistics, different divisors, deliberately.
 */
export function standardize(v: NumericVector): Float64Array | null {
  const n = v.length;
  if (n < MIN_OBSERVATIONS) return null;
  const values = Array.from(v);
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const deviations = values.map((x) => x - mean);
  const sd = Math.sqrt(deviations.reduce((a, d) => a + d * d, 0) / n);
  if (!(sd > 0)) return null;
  return Float64Array.from(deviations, (d) => d / sd);
}

/**
 * Raw Pearson correlation of two ALREADY-ALIGNED vectors, in [-1, 1], or `null`
 * when it is undefined (constant side, or fewer than two observations).
 *
 * Alignment is the CALLER's job and deliberately not inferred here: pairing by
 * position two vectors that were sliced from different date sets produces a
 * perfectly plausible number from mismatched days. `pairwiseAligned` in
 * `pipeline/return-vectors.ts` owns that step.
 *
 * A length mismatch THROWS rather than truncating to the shorter side, for the
 * same reason: truncation is the silent version of the same defect.
 *
 * This is the REFERENCE implementation, not the hot path. The nightly job runs
 * the same arithmetic over pre-standardized vectors in `packages/ingest`; this
 * one is written for legibility and is the 6-dp oracle that keeps that kernel
 * from becoming a second definition of correlation (ADR-0007).
 */
export function pearson(a: NumericVector, b: NumericVector): number | null {
  if (a.length !== b.length) throw new VectorLengthMismatchError(a.length, b.length);
  const za = standardize(a);
  const zb = standardize(b);
  if (za === null || zb === null) return null;
  const dot = za.reduce((acc, x, i) => acc + x * (zb[i] ?? 0), 0);
  /* Float error can push a self-correlation a few ulps past 1; the DB CHECK on
     `correlation_pair.rho` is a hard [-1, 1] and would reject the row. */
  return Math.max(-1, Math.min(1, dot / za.length));
}

function corrImpl(a: FactorSecurity, b: FactorSecurity): number {
  if (a.ticker === b.ticker) return 1;
  const fa = asFactorVector(a.f, 'a');
  const fb = asFactorVector(b.f, 'b');
  /* A zero norm is only reachable with idio === 0, which the generator cannot
     produce. Live rows arriving without loadings would divide 0 by 0 and emit
     NaN — which JSON-serialises to null and renders as a blank matrix cell. */
  if (!(a.nrm > 0) || !(b.nrm > 0)) {
    throw new Error(`corr: factor norm must be positive (${String(a.nrm)}, ${String(b.nrm)})`);
  }
  const cosine = dot(fa, fb) / (a.nrm * b.nrm);
  /* `+ 0` normalises -0 to 0. A small negative cosine rounds to -0, which is a
     meaningless distinction for a correlation and is invisible in every display
     path — but `Object.is(-0, 0)` is false, so it survives as a real difference
     in a Map key or an equality assertion, while `JSON.stringify` erases it. A
     fixture round-trip would then compare -0 against 0 and disagree. */
  const rounded = Math.round(cosine * ROUND_SCALE) / ROUND_SCALE + 0;
  return Math.max(CORR_CLAMP_LO, Math.min(CORR_CLAMP_HI, rounded));
}
